import React, { useState, useEffect, useMemo } from "react";
import { TEAM_PROFILE } from "../../constants/routes";
import MultiSelectDropdown from "../MultiSelectDropdown";
import Pill from "../Pill";
import Spinner from "../Spinner";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import api from "../../utils/api";
import { useParams } from "react-router-dom";
import { useReportIssue } from "../../hooks/useReportIssue";
import { ReportIssueModal } from "../../components/ReportIssueModal";


interface IStats {
  stats: { [key: string]: any };
  stat_mapping: {
    [key: string]: {
      [key: string]: string,
    }
  }
}

interface IFilters {
  seasons: string[],
  substat: string[],
  stat: string[],
};

interface TeamProfileRes {
  season: string;
  TeamName: string;
  stats: {
    [key: string]: string;
  }[];
  stat_mapping: {
    // category
    [key: string]: string[]
  }
}

const TeamstatTable = ({ setTeamData }: { setTeamData: any }) => { 
        const [selectedFilters, setSelectedFilters] = useState<IFilters>({ seasons: [], substat: [], stat: [] });
        const [isLoading, setisLoading] = useState(false);
        const[errorStatus, setErrorStatus] = useState<number | null>(null);
        const [errorMessage, setErrorMessage] = useState<string | null>(null);
        const [startDate, setStartDate] = useState<string | null>(null);
        const [endDate, setEndDate] = useState<string | null>(null);
        const [teamProfileData, setTeamProfileData] = useState<TeamProfileRes | null>(null);  

        const { sportsCode, teamCode } = useParams();

      function handleDropdownChange(key: any, value: any) {
    const updatedFilters: any = { ...selectedFilters };
    if (key === "seasons" || key === "substat" || key === "stat") {
      updatedFilters[key] = updatedFilters[key].includes(value)
        ? updatedFilters[key].filter((item: any) => item !== value)
        : [...updatedFilters[key], value];
    }

    setSelectedFilters(updatedFilters);
    if (!teamProfileData) return;
  }

    const getTotalRow = () => {
  if (!teamProfileData || !teamProfileData.stats?.length) return null;

  const totalRow: any = { season: "TOTAL" };

  const relevantStats = teamProfileData.stats.filter((stat) =>
    selectedFilters.seasons.length > 0
      ? selectedFilters.seasons.includes(stat.season)
      : true
  );

  // If no data after filtering, don't show total
  if (relevantStats.length === 0) return null;

  const statKeys = Object.keys(relevantStats[0] ?? {}).filter(
    (key) => key !== "season"
  );

  for (const key of statKeys) {
    const isPercentageStat = String(key).includes("%");

    const { sum, count } = relevantStats.reduce(
      (acc, row) => {
        const val = row[key];
        const numericVal =
          typeof val === "number"
            ? val
            : typeof val === "string"
            ? parseFloat(val)
            : NaN;

        if (!isNaN(numericVal)) {
          acc.sum += numericVal;
          acc.count += 1;
        }
        return acc;
      },
      { sum: 0, count: 0 }
    );

    if (isPercentageStat) {
      totalRow[key] = count > 0 ? sum / count : 0;
    } else {
      totalRow[key] = sum;
    }
  }

  return totalRow;
};


const handleDeleteFilter = (filter: string) => {
   console.log('Filter to delete:', filter, typeof filter);
  console.log('Current seasons:', selectedFilters.seasons);
  // Check if deleting a season

    const seasonExists = selectedFilters?.seasons?.some(season => String(season) === String(filter));
  
  if (seasonExists) {
    setSelectedFilters({ 
      ...selectedFilters, 
      seasons: selectedFilters.seasons.filter((season) => String(season) !== String(filter)) 
    });
    return;
  }
  if (selectedFilters?.seasons?.includes(filter)) {
    setSelectedFilters({ 
      ...selectedFilters, 
      seasons: selectedFilters.seasons.filter((season) => String(season) !== String(filter)) 
    });
    return;
  }

  // Check if deleting a category (stat)
  const isStatCategory = selectedFilters?.stat?.includes(filter);
  
  if (isStatCategory) {
    // Get the stats in this category
    const categoryStats = teamProfileData?.stat_mapping?.[filter] || [];
    
    // Remove the category
    const updatedStat = selectedFilters.stat.filter((s: string) => s !== filter);
    
    // Remove ALL substats that belong to this category
    const updatedSubstat = selectedFilters.substat.filter(
      (substat: string) => !categoryStats.includes(substat)
    );
    
    setSelectedFilters({
      ...selectedFilters,
      stat: updatedStat,
      substat: updatedSubstat
    });
    return;
  }

  // Check if deleting a substat
  if (selectedFilters?.substat?.includes(filter)) {
    setSelectedFilters({
      ...selectedFilters,
      substat: selectedFilters.substat.filter((s: string) => s !== filter)
    });
    return;
  }
};


  const filteredStatKeys = useMemo(() => {
    if (!teamProfileData || !teamProfileData.stats?.length) return [];

    const allStatsFlat = selectedFilters.stat
      .map((stat: string) => teamProfileData.stat_mapping?.[stat] ?? [])
      .flat();

    const totalRow = getTotalRow();

    let keys = Object.keys(teamProfileData.stats[0] ?? {}).filter(
      (key) => key !== "season"
    );

    // Apply filters only if filters are selected
    if (selectedFilters.stat.length > 0) {
      keys = keys.filter((key) => allStatsFlat.includes(key));
    }
    
    if (selectedFilters.substat.length > 0) {
      keys = keys.filter((key) => selectedFilters.substat.includes(key));
    }

    // Remove columns whose total is 0
    keys = keys.filter((key) => {
      if (!totalRow) return true;
      const val = totalRow[key];
      const numericVal =
        typeof val === "number" ? val : typeof val === "string" ? parseFloat(val) : 0;
      return numericVal !== 0;
    });

    // Sort by total value
    if (totalRow) {
      keys.sort((a, b) => {
        const valA = totalRow[a];
        const valB = totalRow[b];
        const numericA = typeof valA === "number" ? valA : typeof valA === "string" ? parseFloat(valA) : 0;
        const numericB = typeof valB === "number" ? valB : typeof valB === "string" ? parseFloat(valB) : 0;
        return numericB - numericA;
      });
    }

    return keys;
  }, [teamProfileData, selectedFilters, getTotalRow]);

  const getColDefs = () => {
    if (!teamProfileData || !teamProfileData.stats?.length) return [];

    const formatValue = (val: any) => {
      if (val === null || val === undefined) return "-";
      const num = typeof val === "number" ? val : parseFloat(String(val));
      if (isNaN(num)) return String(val);
      return Number.isInteger(num) ? String(num) : num.toFixed(2);
    };

    return [
      {
        field: "season",
        headerName: "SEASON",
        pinned: 'left',
        width: 120,
        sortable: true, // Enable sorting for seasons
        valueFormatter: (params: any) => params.value || "-",
      },
      ...filteredStatKeys.map((key) => ({
        field: key,
        headerName: key,
        width: 120,
        sortable: true, // Enable sorting for stats
        valueFormatter: (params: any) => formatValue(params.value),
      })),
    ];
  };

   useEffect(() => {
      async function fetchData() {
        setisLoading(true);
  
        try {
          const res = await api.post(TEAM_PROFILE, {
            SportCode: sportsCode,
            TeamCode: teamCode,
          });
          console.log("team profile",res)
          const teamProfileRes: TeamProfileRes = res.data;
          setTeamProfileData(teamProfileRes);
  
          const stats: IStats[] = [];
          Object.keys(teamProfileRes?.stats ?? {}).forEach((season: string) => {
            const stat: IStats = { stats: {}, stat_mapping: {} };
            stats.push(stat);
          });
  
          setErrorStatus(null);
          setErrorMessage(null);
  
        } catch (error: any) {
          console.error("Error fetching team profile:", error);
  
          const status = error?.response?.status ?? 0;
          setErrorStatus(status);
  
          if (status >= 500) {
            setErrorMessage("Failed to load team profile.");
          } else {
            setErrorMessage("Something went wrong.");
          }
  
        } finally {
          setisLoading(false);
        }
      }

      if (sportsCode) {
        fetchData();
      }
    }, [ sportsCode, teamCode]);
  
      const reportIssue = useReportIssue();
    
      const handleReportIssue = (errorContext?: string) => {
        console.log('Report issue clicked!');
        reportIssue.openModal({
          ComponentSource: 'TeamDashboard - StatTable',
          ErrorContext: errorContext || errorMessage || '',
          UserContext: {
            sportsCode,
            errorStatus,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
          },
        });
      };


    const handleClearAllFilters = () => {
    setSelectedFilters({ seasons: [], substat: [], stat: [] });
  };
    useEffect(() => {
      console.log("Selected Filters:", selectedFilters);
    }, [selectedFilters]);
  
    // Filter data based on selected seasons
    const filteredData = (teamProfileData?.stats ?? []).filter((stat) =>
      selectedFilters.seasons.length > 0
        ? selectedFilters.seasons.includes(stat.season)
        : true
    );
  
    const totalRow = getTotalRow();
  
    // Logic changed here: filteredData is main data, totalRow is pinned data
    

    return (

        <div className="flex flex-col gap-2">
      <div className="flex flex-row justify-between items-start w-full gap-2">
        <div className="flex flex-col gap-1 flex-1">
          <div className="flex flex-row items-center gap-2 flex-wrap">
            <div className="text-sm font-medium">Filters:-</div>

            <div className="flex gap-2 flex-wrap max-w-[40rem] px-2">
              {[...selectedFilters.stat, ...selectedFilters.seasons, ...selectedFilters.substat].map(
                (filter, index) => (
                  <Pill
                    key={index}
                    text={String(filter)}
                    showCross={true}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteFilter(String(filter));
                    }}
                  />
                )
              )}
            </div>

            {(selectedFilters.stat.length ||
              selectedFilters.seasons.length ||
              selectedFilters.substat.length) > 0 && (
              <button
                onClick={handleClearAllFilters}
                className="ml-2 text-xs text-red-600 hover:text-red-800 underline"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-row gap-2 max-w-[480px] w-full">
          <MultiSelectDropdown
            options={teamProfileData?.stats.map((stat) => stat.season) ?? []}
            options2={[]}
            def="Select Season"
            setSelectedOption={handleDropdownChange}
            selectedOption={selectedFilters}
            filterKey="seasons"
            disabled={false}
          />

          <MultiSelectDropdown
            options={Object.keys(teamProfileData?.stat_mapping ?? {}) ?? []}
            options2={[]}
            def="Select Category"
            setSelectedOption={handleDropdownChange}
            selectedOption={selectedFilters}
            filterKey="stat"
            disabled={false}
          />

          <MultiSelectDropdown
            options={
              selectedFilters.stat.length > 0
                ? selectedFilters.stat
                    .map((stat) => teamProfileData?.stat_mapping?.[stat])
                    .flat()
                    .filter((item): item is string => item !== undefined)
                : []
            }
            options2={[]}
            def="Select Substat"
            setSelectedOption={handleDropdownChange}
            selectedOption={selectedFilters}
            filterKey="substat"
            disabled={!selectedFilters.stat.length}
          />
        </div>
      </div>

      {errorMessage && errorStatus >= 500 ? (
        <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
          {errorMessage}{" "}
          <button
            className="underline text-blue-600 hover:text-blue-800"
            onClick={() =>
              handleReportIssue(
                `Failed to fetch team profile ( SportCode: ${sportsCode}, HTTP ${errorStatus})`
              )
            }
          >
            Click here to report this issue
          </button>
        </div>
      ) : (
        <>
          <div className="w-full overflow-x-auto rounded-lg">
            <div
              className="ag-theme-alpine"
              style={{ height: "500px", width: "100%" }}
            >
              <AgGridReact
                // 1. Only actual data goes here
                rowData={filteredData}
                
                // 2. The total row goes here, preventing it from sorting
                pinnedBottomRowData={totalRow ? [totalRow] : []}
                
                columnDefs={getColDefs()}
                defaultColDef={{
                  width: 120,
                  minWidth: 80,
                  resizable: true,
                  sortable: true,
                }}
                getRowStyle={(params) => {
                  if (params.node.rowPinned === 'bottom') {
                    return {
                      fontWeight: "bold",
                      backgroundColor: "#e5e7eb", // Light gray background for total
                      borderTop: "2px solid #ccc"
                    };
                  }
                  return undefined;
                }}
                rowHeight={50}
                theme={themeQuartz}
                loadingOverlayComponent={Spinner}
                loading={isLoading}
                suppressHorizontalScroll={false}
                alwaysShowHorizontalScroll={true}
              />
            </div>
          </div>
        </>
      )}

      <ReportIssueModal
        isOpen={reportIssue.isModalOpen}
        onClose={reportIssue.closeModal}
        onSubmit={reportIssue.handleSubmit}
        issue={reportIssue.issue}
        setIssue={reportIssue.setIssue}
        feedbackMessage={reportIssue.feedbackMessage}
        feedbackType={reportIssue.feedbackType}
        isLoading={reportIssue.isLoading}
      />
    </div>
    );
 }


export default TeamstatTable;